import React, { useState } from "react";
import { useToast } from "../../Hooks/toast";
import { Dialog, Card, Button, Col, Divider, H4, H6, FormTextArea } from '../../Elements'
import { Classes, Intent } from "@blueprintjs/core";
import { useLazyQuery } from "../../Hooks/graphql_utils";
import { gql } from "@apollo/client";

const SEND_EMAIL_QUERY = gql`
    query sendBuilderEmail($template_name: String!, $job_id: String!, $job_detail_id: Int!, $comment: String, $email_type: String!) {
        sendBuilderEmail(template_name: $template_name, job_id: $job_id, job_detail_id: $job_detail_id, 
                            comment: $comment, email_type: $email_type) {
            is_sent
            response_text
        }
    }
`

export const BuilderEmailDialog = ({
    data,
    open,
    setOpen,
    usePortal = true,
    jobId,
    jobDetailId,
    hasContactEmail,
    hasSupervisorEmail
}) => {
    if (!data) {
        return null
    }

    return (
        <Dialog
            isOpen={open}
            canEscapeKeyClose={true}
            canOutsideClickClose={true}
            onClose={() => setOpen(!open)}
            usePortal={usePortal}
            title='Send Email to Builder and Supervisor'
            icon='envelope'
        >
            <div className={Classes.DIALOG_BODY}>
                <Card>
                    <Col>
                        {data.map((row, index, arr) => {
                            const emailType = row?.is_pebble ? 'pebble' :
                                row?.is_gunite ? 'gunite' :
                                    row?.is_warranty ? 'warranty' :
                                        null
                            return (
                                <>
                                    <EmailTemplateRow
                                        key={index}
                                        displayName={row.display_name}
                                        awsName={row.aws_name}
                                        jobId={jobId}
                                        jobDetailId={jobDetailId}
                                        hasContactEmail={hasContactEmail}
                                        hasSupervisorEmail={hasSupervisorEmail}
                                        emailType={emailType}
                                    />
                                    {(index !== arr.length - 1) && <Divider />}
                                </>
                            );
                        })}
                        {data.length === 0 && <H4>No email templates available.</H4>}
                    </Col>
                </Card>
            </div>
        </Dialog>
    )
}

const EmailTemplateRow = ({ displayName, awsName, jobId, jobDetailId, hasContactEmail, hasSupervisorEmail, emailType }) => {
    const [textAreaOpen, setTextAreaOpen] = useState(false)
    const [textAreaValue, setTextAreaValue] = useState('')
    const [clickedQuickSend, setClickedQuickSend] = useState(false)
    const [clickedSendWithMessage, setClickedSendWithMessage] = useState(false)
    const [isResponseError, setIsResponseError] = useState(false)
    const hasSentEmail = clickedQuickSend || clickedSendWithMessage
    const { showSuccess, showError } = useToast()

    let successMessage
    if (hasContactEmail && hasSupervisorEmail) {
        successMessage = "Successfully sent email to both company contact and supervisor!"
    } else if (hasContactEmail) {
        successMessage = "Successfully sent email to company contact!"
    } else if(hasSupervisorEmail) {
        successMessage = "Successfully sent email to supervisor!"
    }

    const [sendEmail, { loading, error, data }] = useLazyQuery(SEND_EMAIL_QUERY, {
        fetchPolicy: 'network-only',
        onCompleted: res => {
            if (res.sendBuilderEmail?.is_sent) {
                const message = res.sendBuilderEmail.response_text?.includes('test recipient')
                    ? res.sendBuilderEmail.response_text
                    : successMessage
                showSuccess(message)
                setIsResponseError(false)
            }
            else {
                showError(`An unexpected error occurred and the email was not sent.
                        \n Error message: ${res.sendBuilderEmail?.response_text}`)
                setIsResponseError(true)
            }
        }
    })

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        textAlign: 'left',
                        alignItems: 'center'
                    }}>
                    <H6
                        style={{ marginBottom: 0 }}
                    >
                        Template: {displayName}
                    </H6>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'right'
                    }}
                >
                    <Button
                        icon={error || isResponseError ? "error" : clickedQuickSend ? "tick" : "send-message"}
                        intent={error || isResponseError ? Intent.DANGER : clickedQuickSend && data ? Intent.SUCCESS : Intent.NONE}
                        disabled={textAreaOpen || loading || hasSentEmail}
                        onClick={() => {
                            setClickedQuickSend(true)
                            sendEmail({
                                variables: {
                                    template_name: awsName,
                                    job_detail_id: jobDetailId,
                                    job_id: String(jobId),
                                    comment: null,
                                    email_type: emailType
                                },
                            })
                        }}
                        loading={loading && clickedQuickSend}
                    />
                    <Button
                        icon={textAreaOpen ? "minimize" : "comment"}
                        intent={clickedSendWithMessage && data ? Intent.SUCCESS : Intent.NONE}
                        disabled={loading}
                        onClick={() => setTextAreaOpen(!textAreaOpen)}
                    />
                </div>
            </div>
            {textAreaOpen &&
                <div>
                    <FormTextArea
                        placeholder='Enter a comment...'
                        value={textAreaValue}
                        onChange={e => setTextAreaValue(e.target.value)}
                        maxlength={1000}
                        style={{ resize: 'none', marginTop: 10 }}
                        disabled={hasSentEmail}
                    />
                    <Button
                        icon={error || isResponseError ? "error" : clickedSendWithMessage ? "tick" : "send-message"}
                        intent={error || isResponseError ? Intent.DANGER : clickedSendWithMessage && data ? Intent.SUCCESS : Intent.NONE}
                        disabled={textAreaValue === '' || loading || hasSentEmail}
                        onClick={() => {
                            setClickedSendWithMessage(true)
                            sendEmail({
                                variables: {
                                    template_name: awsName,
                                    job_detail_id: jobDetailId,
                                    job_id: String(jobId),
                                    comment: textAreaValue,
                                    email_type: emailType
                                }
                            })
                        }}
                        loading={loading && clickedSendWithMessage}
                    >
                        Send email with comment
                    </Button>
                </div>}
        </>
    )
}
